import type React from "react";
import { useEffect, useState } from "react";

import type { ConnectedProjectDto } from "@/api/types";
import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import {
  CLEARED_PORTFOLIO_PROJECT_FILTER,
  PortfolioProjectFilterFields,
  type PortfolioProjectFilterValues,
} from "@/components/ProjectFilter/PortfolioProjectFilterFields";
import { Button } from "@/design-system/Button";
import { useTranslation } from "@/translations";

type PortfolioProjectFilterDrawerProps = DrawerBaseProps & {
  selectedFilter: PortfolioProjectFilterValues;
  onUpdate: (value: PortfolioProjectFilterValues) => void;
  projects: ConnectedProjectDto[];
  /** Shows an additional project selector after the portfolio attribute filters. */
  includeProjectSelection?: boolean;
};

/** Edits portfolio project filters as a draft and applies or clears them when the drawer closes. */
export function PortfolioProjectFilterDrawer({
  projects,
  selectedFilter,
  onUpdate,
  includeProjectSelection = false,
  ...baseProps
}: PortfolioProjectFilterDrawerProps): React.ReactNode {
  const [currFilter, setCurrFilter] = useState<PortfolioProjectFilterValues>(selectedFilter);
  const { t } = useTranslation();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrFilter(selectedFilter);
  }, [selectedFilter]);

  const handleApplyFilter = () => {
    onUpdate(currFilter);
  };

  const handleClearFilter = () => {
    setCurrFilter(CLEARED_PORTFOLIO_PROJECT_FILTER);
    onUpdate(CLEARED_PORTFOLIO_PROJECT_FILTER);
  };

  return (
    <Drawer.Root
      title={t("page.portfolio.overview.project-filter-drawer.title")}
      {...baseProps}
      data-testid="portfolio-project-filter-drawer"
    >
      <Drawer.Body>
        <PortfolioProjectFilterFields
          projects={projects}
          value={currFilter}
          onChange={setCurrFilter}
          includeProjectSelection={includeProjectSelection}
        />
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClearFilter} data-testid="clear-filters-button">
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button onClick={handleApplyFilter} data-testid="apply-filters-button">
            {t("common.action.apply")}
          </Button>
        </Drawer.Close>
      </Drawer.Actions>
    </Drawer.Root>
  );
}
